/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { AccessStatus } from './useAccessStatus'

const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

interface WaitlistUser {
  id: string
  email: string
  full_name: string | null
  created_at: string
  inquiry_count: number
  has_inquiry: boolean
}

interface WaitlistInquiry {
  id: string
  user_id: string
  user_email: string
  user_full_name: string | null
  reason: string
  message: string | null
  created_at: string
  reviewed_at: string | null
  reviewed_by: string | null
  is_reviewed: boolean
}

/**
 * Fetch all waitlisted users (admin only)
 */
export function useWaitlistUsers(params?: { search?: string; has_inquiry?: boolean }) {
  return useQuery({
    queryKey: ['admin', 'waitlist', 'users', params],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      // Build query string
      const queryParams = new URLSearchParams()
      if (params?.search) queryParams.set('search', params.search)
      if (params?.has_inquiry !== undefined) queryParams.set('has_inquiry', String(params.has_inquiry))

      const url = `${API_BASE}/api/admin/waitlist${queryParams.toString() ? '?' + queryParams.toString() : ''}`

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Admin access required')
        }
        throw new Error(`Failed to fetch waitlist users: ${response.status}`)
      }

      return await response.json() as WaitlistUser[]
    },
  })
}

/**
 * Fetch all waitlist inquiries (admin only)
 */
export function useWaitlistInquiries(params?: { reviewed?: boolean }) {
  return useQuery({
    queryKey: ['admin', 'waitlist', 'inquiries', params],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      // Build query string
      const queryParams = new URLSearchParams()
      if (params?.reviewed !== undefined) queryParams.set('reviewed', String(params.reviewed))

      const url = `${API_BASE}/api/admin/waitlist/inquiries${queryParams.toString() ? '?' + queryParams.toString() : ''}`

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Admin access required')
        }
        throw new Error(`Failed to fetch inquiries: ${response.status}`)
      }

      return await response.json() as WaitlistInquiry[]
    },
  })
}

/**
 * Update a user's access status (admin only)
 */
export function useUpdateAccessStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, accessStatus }: { userId: string; accessStatus: AccessStatus }) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/admin/users/${userId}/access-status?access_status=${accessStatus}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to update access status')
      }

      return response.json()
    },
    onSuccess: () => {
      // Refresh relevant queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'waitlist'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

/**
 * Mark an inquiry as reviewed (admin only)
 */
export function useMarkInquiryReviewed() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (inquiryId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/admin/waitlist/inquiries/${inquiryId}/reviewed`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to mark inquiry as reviewed')
      }

      return response.json()
    },
    onSuccess: () => {
      // Refresh inquiries
      queryClient.invalidateQueries({ queryKey: ['admin', 'waitlist', 'inquiries'] })
    },
  })
}
